import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { reportsGateway } from '../index';
import { formatReportCurrency, formatReportDate } from '../formatting';
import type { PayrollRegisterResponse, PayrollRunSummary } from '../types';

function runLabel(run: PayrollRunSummary): string {
  return `${formatReportDate(run.periodStartDate)} – ${formatReportDate(run.periodEndDate)} · Check date ${formatReportDate(run.checkDate)}`;
}

/** Payroll Details report (EPN-76). A read-only, per-employee view of the existing Payroll Register
 * projection (`ReportsGateway.getPayrollRegister`) — no separate Payroll Details API exists or is needed. */
export function PayrollDetailsPage() {
  // EPN-77: an optional ?run=<id> lets the Payroll Journal report's drilldown land on the same payroll
  // run it was viewing, instead of always falling back to the most recent approved run.
  const [searchParams] = useSearchParams();
  const requestedRunId = searchParams.get('run');

  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [register, setRegister] = useState<PayrollRegisterResponse | null>(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    reportsGateway
      .listApprovedPayrollRuns(controller.signal)
      .then((result) => {
        const sorted = [...result].sort((a, b) => b.checkDate.localeCompare(a.checkDate));
        setRuns(sorted);
        const requested = requestedRunId && sorted.some((r) => r.id === requestedRunId) ? requestedRunId : null;
        setSelectedRunId(requested ?? sorted[0]?.id ?? null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRunsError(friendlyErrorMessage(err, 'Could not load approved payroll runs.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setRunsLoading(false);
      });
    return () => controller.abort();
    // Intentionally mount-only: requestedRunId is only ever meant to seed the initial selection from the
    // URL this page was opened with, never to re-run this fetch if the query string changes later.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedRunId) return;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRegisterLoading(true);
    setRegisterError(null);
    setRegister(null);
    reportsGateway
      .getPayrollRegister(selectedRunId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setRegister(result);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRegisterError(friendlyErrorMessage(err, 'Could not load the payroll details for this run.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setRegisterLoading(false);
      });
    return () => controller.abort();
  }, [selectedRunId]);

  const selectedRun = runs.find((r) => r.id === selectedRunId) ?? null;

  return (
    <section className="card card--wide" aria-labelledby="payroll-details-heading">
      <div className="section-header">
        <h2 id="payroll-details-heading">Payroll Details</h2>
      </div>

      {runsLoading && <p className="muted" role="status">Loading approved payroll runs…</p>}
      {runsError && <p className="error" role="alert">{runsError}</p>}
      {!runsLoading && !runsError && runs.length === 0 && (
        <p className="muted">No approved payroll runs yet. Payroll Details are available once a payroll run is approved.</p>
      )}

      {!runsLoading && runs.length > 0 && (
        <div>
          <label htmlFor="payroll-details-run">Payroll run </label>
          <select
            id="payroll-details-run"
            value={selectedRunId ?? ''}
            onChange={(e) => setSelectedRunId(e.target.value)}
          >
            {runs.map((r) => (
              <option key={r.id} value={r.id}>{runLabel(r)}</option>
            ))}
          </select>
        </div>
      )}

      {registerLoading && <p className="muted" role="status">Loading payroll details…</p>}
      {registerError && <p className="error" role="alert">{registerError}</p>}

      {register && selectedRun && (
        <>
          <dl aria-label="Pay period and check date">
            <div><dt>Pay period</dt><dd>{formatReportDate(register.periodStartDate)} – {formatReportDate(register.periodEndDate)}</dd></div>
            <div><dt>Check date</dt><dd>{formatReportDate(register.checkDate)}</dd></div>
            <div><dt>Status</dt><dd>{register.status}</dd></div>
          </dl>

          {register.lines.length === 0 ? (
            <p className="muted">No employee payroll details for this payroll run.</p>
          ) : (
            <table aria-label="Payroll details by employee">
              <thead>
                <tr>
                  <th scope="col">Employee</th>
                  <th scope="col">Gross Earnings</th>
                  <th scope="col">Employee Taxes</th>
                  <th scope="col">Employer Taxes</th>
                  <th scope="col">Pre-Tax Deductions</th>
                  <th scope="col">Post-Tax Deductions</th>
                  <th scope="col">Garnishments</th>
                  <th scope="col">Net Pay</th>
                </tr>
              </thead>
              <tbody>
                {register.lines.map((line) => (
                  <tr key={line.employeeId}>
                    <th scope="row">{line.employeeNumber} — {line.employeeName}</th>
                    <td>{formatReportCurrency(line.grossEarnings)}</td>
                    <td>{formatReportCurrency(line.employeeTaxes)}</td>
                    <td>{formatReportCurrency(line.employerTaxes)}</td>
                    <td>{formatReportCurrency(line.preTaxDeductionsTotal)}</td>
                    <td>{formatReportCurrency(line.postTaxDeductionsTotal)}</td>
                    <td>{formatReportCurrency(line.garnishmentsTotal)}</td>
                    <td>{formatReportCurrency(line.netPay)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Totals</th>
                  <td>{formatReportCurrency(register.totals.grossEarnings)}</td>
                  <td>{formatReportCurrency(register.totals.employeeTaxes)}</td>
                  <td>{formatReportCurrency(register.totals.employerTaxes)}</td>
                  <td>{formatReportCurrency(register.totals.preTaxDeductionsTotal)}</td>
                  <td>{formatReportCurrency(register.totals.postTaxDeductionsTotal)}</td>
                  <td>{formatReportCurrency(register.totals.garnishmentsTotal)}</td>
                  <td>{formatReportCurrency(register.totals.netPay)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </>
      )}
    </section>
  );
}
